// REFACTORED: 2026-02-03
// Projects page - Metrics Section (Total, Client, Internal projects)

import React from 'react';
import { Grid, Card, Skeleton } from '@mui/material';
import {
  Assessment as SummaryIcon,
  Business as InternalIcon,
  BusinessCenter as ClientIcon,
  AttachMoney as BookedIcon,
  TrendingUp as RecognizedIcon,
  AccountBalanceWallet as RealizedIcon,
} from '@mui/icons-material';
import { MiniMetricCard, formatCompactUsd } from '../../cards/MetricCards';
import { METRIC_CARD_APPLE_RADIUS } from '../../cards/metricCardTheme';
import { SEARCE_COLORS } from '../../../theme';
import { purple } from '@mui/material/colors';

interface ProjectMetricsSectionProps {
  totalProjects: number;
  internalProjects: number;
  totalBookedRevenue?: number;
  totalRecognizedRevenue?: number;
  totalRealizedRevenue?: number;
  isLoading?: boolean;
}

/**
 * PROJECT METRICS SECTION
 *
 * Displays 3 key project count metrics:
 * - Total Projects (all projects)
 * - Client Projects (total - internal)
 * - Internal Projects
 */
const ProjectMetricsSection: React.FC<ProjectMetricsSectionProps> = ({
  totalProjects,
  internalProjects,
  totalBookedRevenue = 0,
  totalRecognizedRevenue = 0,
  totalRealizedRevenue = 0,
  isLoading = false,
}) => {
  const clientProjects = totalProjects - internalProjects;

  if (isLoading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card variant="outlined" sx={{ p: 2, height: "100%", boxShadow: "none", borderRadius: METRIC_CARD_APPLE_RADIUS }}>
              <Skeleton variant="text" width="60%" height={22} sx={{ mb: 1.5 }} animation="wave" />
              <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} animation="wave" />
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={2}>
        <MiniMetricCard
          title="Total Projects"
          value={totalProjects || 0}
          icon={SummaryIcon}
          color={SEARCE_COLORS.primary}
          tooltip="All projects returned for the current Projects page filters (client + internal)."
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <MiniMetricCard
          title="Client Projects"
          value={clientProjects || 0}
          icon={ClientIcon}
          color={SEARCE_COLORS.success}
          tooltip="Projects not funded as internal Searce Fund (Emea/India); total minus internal count."
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <MiniMetricCard
          title="Internal Projects"
          value={internalProjects || 0}
          icon={InternalIcon}
          color={SEARCE_COLORS.info}
          tooltip="Projects under internal Searce Fund accounts (Emea / India) in the filtered summary."
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <MiniMetricCard
          title="Total Booked Revenue"
          value={formatCompactUsd(totalBookedRevenue)}
          icon={BookedIcon}
          color={SEARCE_COLORS.warning}
          tooltip="Sum of project_revenue_converted across all filtered projects."
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <MiniMetricCard
          title="Total Recognized Revenue"
          value={formatCompactUsd(totalRecognizedRevenue)}
          icon={RecognizedIcon}
          color={SEARCE_COLORS.primary}
          tooltip="Sum of recognized_revenue across all filtered projects."
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <MiniMetricCard
          title="Total Realized Revenue"
          value={formatCompactUsd(totalRealizedRevenue)}
          icon={RealizedIcon}
          color={SEARCE_COLORS.success}
          tooltip="Sum of realized_revenue across all filtered projects."
        />
      </Grid>
    </Grid>
  );
};

export default ProjectMetricsSection;

